import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  buildLogisticsProgressQueryPayload,
  isTrackedLogisticsAsset,
  normalizeLogisticsProgressQueryData,
} from "../models/logisticsProgressPayload";
import { queryLogisticsProgress } from "../services/logisticsProgressService";

const PROGRESS_CACHE_TIME = 5 * 60 * 1000;

export const LOGISTICS_PROGRESS_QUERY_KEY = "logistics-progress";

export const getLogisticsProgressIdentity = (item = {}) =>
  item?.uid ||
  (item?.objectType && item?.objectId !== undefined && item?.objectId !== null
    ? `${item.objectType}:${item.objectId}`
    : "");

// Equipment is the parent container asset and is never tracked in
// Logistics — only its tiers/components/instruments are.
export const isLogisticsInsightsCountableAsset = isTrackedLogisticsAsset;

export const getUniqueLogisticsProgressItems = (items = []) => {
  const itemLookup = new Map();

  items.filter(Boolean).forEach((item) => {
    const identity = getLogisticsProgressIdentity(item);

    if (identity && item.objectType && item.objectId !== undefined) {
      itemLookup.set(identity, item);
    }
  });

  return Array.from(itemLookup.entries())
    .sort(([leftIdentity], [rightIdentity]) =>
      leftIdentity.localeCompare(rightIdentity),
    )
    .map(([, item]) => item);
};

export const getLogisticsProgressQueryKey = (items = []) => [
  LOGISTICS_PROGRESS_QUERY_KEY,
  getUniqueLogisticsProgressItems(items)
    .map(getLogisticsProgressIdentity)
    .join("|"),
];

export const getLogisticsProgressQueryOptionsKey = ({
  includeStatistics = false,
} = {}) => (includeStatistics ? "statistics" : "progress-only");

export const applyProgressUpdateToResults = (
  currentResults = [],
  assets = [],
  logistics = {},
) => {
  const resultLookup = new Map(
    currentResults.map((result) => [
      getLogisticsProgressIdentity(result),
      result,
    ]),
  );

  getUniqueLogisticsProgressItems(assets).forEach((asset) => {
    const identity = getLogisticsProgressIdentity(asset);
    const existing = resultLookup.get(identity) || {};

    resultLookup.set(identity, {
      ...existing,
      uid: asset.uid || identity,
      objectType: asset.objectType,
      objectId: String(asset.objectId),
      logisticsStage: logistics.mainStage || "NOT_STARTED",
      logisticsMilestone: logistics.milestone || "",
      logisticsStatus: logistics.status || "NOT_STARTED",
      logisticsProgressPercent: Number(logistics.progressPercent ?? 0),
      logisticsNotes: logistics.notes?.trim() || "",
      logisticsDetailText: logistics.detailText?.trim() || "",
      logisticsDetailDate: logistics.detailDate || "",
    });
  });

  return Array.from(resultLookup.values());
};

const useLogisticsProgressQuery = (items = [], options = {}) => {
  const { includeStatistics = false } = options;
  // Equipment must never reach the Logistics query payload — only its
  // tracked children (tiers/components/instruments) do.
  const targets = useMemo(
    () => getUniqueLogisticsProgressItems(items).filter(isTrackedLogisticsAsset),
    [items],
  );
  const queryKey = useMemo(
    () => [
      ...getLogisticsProgressQueryKey(targets),
      getLogisticsProgressQueryOptionsKey({ includeStatistics }),
    ],
    [includeStatistics, targets],
  );

  const query = useQuery({
    queryKey,
    enabled: targets.length > 0,
    staleTime: PROGRESS_CACHE_TIME,
    queryFn: async () => {
      const payload = buildLogisticsProgressQueryPayload(targets, {
        includeStatistics,
      });
      const data = await queryLogisticsProgress(payload);

      return normalizeLogisticsProgressQueryData(data);
    },
  });

  return {
    progressResults: query.data?.results || [],
    statistics: query.data?.statistics || null,
    isLoading: query.isLoading,
    error: query.error,
    queryKey,
  };
};

export default useLogisticsProgressQuery;
